/**
 * @fileoverview
 * Image Popup Component - Advanced image viewer with controls
 * 
 * Features:
 * - Zoom in/out with mouse wheel and buttons
 * - Pan/drag functionality
 * - Rotate left/right
 * - Fullscreen toggle
 * - Download image
 * - Open in new tab
 * - Reset view
 * 
 * @version 2.0.0
 * @author KATALYST TEAM
 */

import { 
  Component, 
  inject, 
  signal, 
  computed, 
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../modal/modal.component';
import { ImageService } from '@core/services/image.service';

@Component({
  selector: 'app-image-popup',
  standalone: true,
  imports: [CommonModule, ModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './image-popup.component.html',
  styleUrl: './image-popup.component.css',
})
export class ImagePopupComponent {
  @ViewChild('imageElement') imageElement!: ElementRef<HTMLImageElement>;

  imageService = inject(ImageService);

  // Zoom state
  private readonly MIN_ZOOM = 0.1;
  private readonly MAX_ZOOM = 5;
  private readonly ZOOM_STEP = 0.25;
  
  zoom = signal(1);
  zoomPercent = computed(() => Math.round(this.zoom() * 100));

  // Rotation state (in degrees)
  rotation = signal(0);

  // Pan/drag state
  panX = signal(0);
  panY = signal(0);
  isDragging = signal(false);
  private dragStartX = 0;
  private dragStartY = 0;
  private panStartX = 0;
  private panStartY = 0;

  // Fullscreen state
  isFullscreen = signal(false);

  // Computed transform for the image
  imageTransform = computed(() => {
    const z = this.zoom();
    const r = this.rotation();
    const x = this.panX();
    const y = this.panY();
    return `translate(${x}px, ${y}px) scale(${z}) rotate(${r}deg)`;
  });

  /**
   * Zoom in by step
   */
  zoomIn(): void {
    const newZoom = Math.min(this.zoom() + this.ZOOM_STEP, this.MAX_ZOOM);
    this.zoom.set(newZoom);
  }

  /**
   * Zoom out by step
   */
  zoomOut(): void {
    const newZoom = Math.max(this.zoom() - this.ZOOM_STEP, this.MIN_ZOOM);
    this.zoom.set(newZoom);
  }

  /**
   * Handle mouse wheel for zoom
   */
  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -this.ZOOM_STEP : this.ZOOM_STEP;
    const newZoom = Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, this.zoom() + delta));
    this.zoom.set(newZoom);
  }

  /**
   * Rotate image left (counter-clockwise)
   */
  rotateLeft(): void {
    this.rotation.update(r => (r - 90) % 360);
  }

  /**
   * Rotate image right (clockwise)
   */
  rotateRight(): void {
    this.rotation.update(r => (r + 90) % 360);
  }

  /**
   * Reset view to default
   */
  resetView(): void {
    this.zoom.set(1);
    this.rotation.set(0);
    this.panX.set(0);
    this.panY.set(0);
  }

  /**
   * Toggle fullscreen mode
   */
  toggleFullscreen(): void {
    this.isFullscreen.update(v => !v);
  }

  /**
   * Start drag operation
   */
  onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return; // Only left mouse button
    
    this.isDragging.set(true);
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.panStartX = this.panX();
    this.panStartY = this.panY();
  }

  /**
   * Handle drag movement
   */
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging()) return;

    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;

    this.panX.set(this.panStartX + deltaX);
    this.panY.set(this.panStartY + deltaY);
  }

  /**
   * End drag operation
   */
  onMouseUp(): void {
    this.isDragging.set(false);
  }

  /**
   * Download the image
   */
  downloadImage(): void {
    const url = this.imageService.popupImageUrl();
    if (!url) return;

    const link = document.createElement('a');
    link.href = url;
    link.download = this.generateFileName();
    link.target = '_blank';
    
    // For cross-origin images, open in new tab instead
    window.open(url, '_blank');
  }

  /**
   * Open image in new tab
   */
  openInNewTab(): void {
    const url = this.imageService.popupImageUrl();
    if (url) {
      window.open(url, '_blank');
    }
  }

  /**
   * Handle image load success
   */
  onImageLoad(): void {
    // Reset view when new image loads
    this.resetView();
  }

  /**
   * Handle image load error
   */
  onImageError(event: Event): void {
    console.error('Failed to load image');
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  /**
   * Handle popup close
   */
  onClose(): void {
    this.resetView();
    this.isFullscreen.set(false);
    this.imageService.closePopup();
  }

  /**
   * Generate filename for download
   */
  private generateFileName(): string {
    const title = this.imageService.popupTitle() || 'image';
    const sanitized = title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const timestamp = new Date().toISOString().slice(0, 10);
    return `${sanitized}_${timestamp}.jpg`;
  }
}
